import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Heart, Trash2, Loader2, Send, Search } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Attachments, FilePicker } from "@/components/Attachments";
import { subjectStyle } from "@/lib/subjects";
import { ReportButton } from "@/components/ReportButton";

const ago = (ts) => {
  const m = Math.round((Date.now() - new Date(ts)) / 60000);
  return m < 1 ? "just now" : m < 60 ? `${m}m ago` : m < 1440 ? `${Math.round(m / 60)}h ago` : new Date(ts).toLocaleDateString("en-GB", { day: "numeric", month: "short" });
};

const Composer = ({ kind, gid, onDone }) => {
  const [title, setTitle] = useState("");
  const [subject, setSubject] = useState("");
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const notes = kind === "notes";
  const ok = notes ? title.trim() : text.trim() || files.length;
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    const fd = new FormData();
    fd.append("title", title); fd.append("subject", subject); fd.append("text", text);
    files.forEach((f) => fd.append("files", f));
    try { await api.post(`/groups/${gid}/${kind}`, fd); setTitle(""); setSubject(""); setText(""); setFiles([]); toast.success(notes ? "Note shared" : "Posted"); onDone(); }
    catch (err) { toast.error(formatApiError(err)); }
    setBusy(false);
  };
  const field = "w-full rounded-2xl border border-slate-200 bg-surface-deep/60 px-3.5 py-2.5 text-sm text-white placeholder:text-slate-400 outline-none focus:border-gold-400";
  return (
    <form onSubmit={submit} data-testid={`${kind}-composer`} className="card p-4 sm:p-5 space-y-2.5">
      {notes && (
        <div className="flex gap-2">
          <input data-testid="note-title-input" value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} placeholder="Note title, e.g. Ionic bonding summary" className={field} />
          <input data-testid="note-subject-input" value={subject} onChange={(e) => setSubject(e.target.value)} maxLength={60} placeholder="Subject" className={`${field} !w-36 shrink-0`} />
        </div>
      )}
      <textarea data-testid={`${kind}-text-input`} value={text} onChange={(e) => setText(e.target.value)} rows={notes ? 4 : 2} maxLength={4000}
        placeholder={notes ? "Type your notes (optional if you attach photos or a PDF)" : "Share something with the group"} className={field} />
      <div className="flex flex-wrap items-center justify-between gap-2">
        <FilePicker files={files} setFiles={setFiles} id={`${kind}-file`} />
        <button data-testid={`${kind}-submit`} disabled={busy || !ok} className="inline-flex items-center gap-2 rounded-full bg-gold-400 px-5 py-2.5 text-sm font-bold text-black hover:bg-gold-300 disabled:opacity-40">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}{notes ? "Share note" : "Post"}
        </button>
      </div>
    </form>
  );
};

const Item = ({ it, kind, gid, me, isOwner, reload }) => {
  const idk = kind === "notes" ? "note_id" : "post_id";
  const id = it[idk];
  const liked = (it.likes ?? []).includes(me);
  const like = async () => { await api.post(`/groups/${gid}/${kind}/${id}/like`).catch(() => {}); reload(); };
  const del = async () => { if (!window.confirm("Delete this?")) return; await api.delete(`/groups/${gid}/${kind}/${id}`).catch(() => {}); reload(); };
  return (
    <li data-testid={`${kind}-item-${id}`} className="card p-4 sm:p-5">
      <div className="flex items-center gap-3">
        <span className="grid place-items-center w-9 h-9 shrink-0 rounded-full bg-navy-200 text-sm font-bold text-gold-300">{(it.name || "?").slice(0, 1)}</span>
        <div className="flex-1 min-w-0"><p className="text-sm font-semibold text-white">{it.name}</p><p className="text-xs text-slate-500">{ago(it.ts)}</p></div>
        {it.subject && <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${subjectStyle(it.subject).chip}`}>{it.subject}</span>}
      </div>
      {it.title && <h3 className="mt-3 font-display text-2xl text-white">{it.title}</h3>}
      {it.text && <p className="mt-2 text-sm text-slate-700 whitespace-pre-wrap break-words">{it.text}</p>}
      <Attachments files={it.files} />
      <div className="mt-3 flex items-center gap-2">
        <button data-testid={`${kind}-like-${id}`} onClick={like} className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-semibold transition-colors ${liked ? "border-brand bg-brand/20 text-rose-600" : "border-slate-200 text-slate-600 hover:text-white"}`}>
          <Heart className={`w-3.5 h-3.5 ${liked ? "fill-current" : ""}`} />{(it.likes ?? []).length}
        </button>
        {it.user_id !== me && <ReportButton kind={kind === "notes" ? "note" : "post"} itemId={id} scopeId={gid} id={`${kind}-report-${id}`} />}
        {(it.user_id === me || isOwner) && <button data-testid={`${kind}-delete-${id}`} onClick={del} aria-label="Delete" className="ml-auto grid place-items-center w-8 h-8 rounded-lg text-slate-500 hover:bg-rose-600 hover:text-white"><Trash2 className="w-4 h-4" /></button>}
      </div>
    </li>
  );
};

export const GroupBoard = ({ gid, kind, me, isOwner }) => {
  const [items, setItems] = useState(null);
  const [q, setQ] = useState("");
  const load = () => api.get(`/groups/${gid}/${kind}`).then((r) => setItems(Array.isArray(r.data) ? r.data : [])).catch(() => setItems([]));
  useEffect(() => { setItems(null); load(); }, [gid, kind]); // eslint-disable-line react-hooks/exhaustive-deps
  const shown = (items ?? []).filter((it) => !q || `${it.title} ${it.subject} ${it.text}`.toLowerCase().includes(q.toLowerCase()));
  return (
    <section data-testid={`group-${kind}`} className="space-y-4">
      <Composer kind={kind} gid={gid} onDone={load} />
      {kind === "notes" && (items ?? []).length > 3 && (
        <label className="flex items-center gap-2 rounded-2xl border border-slate-200 bg-surface px-3.5 h-11"><Search className="w-4 h-4 text-slate-500" />
          <input data-testid="notes-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search notes by title or subject" className="flex-1 bg-transparent text-sm text-white outline-none placeholder:text-slate-400" />
        </label>
      )}
      {items === null && <div className="h-32 rounded-3xl bg-surface animate-pulse" />}
      {items?.length === 0 && <p className="rounded-3xl border-2 border-dashed border-slate-200 p-8 text-center text-slate-500">{kind === "notes" ? "No notes yet. Share your first one above." : "No posts yet. Start the conversation."}</p>}
      <ul className="space-y-3">{shown.map((it) => <Item key={it.note_id || it.post_id} it={it} kind={kind} gid={gid} me={me} isOwner={isOwner} reload={load} />)}</ul>
    </section>
  );
};
